import React, { Suspense, lazy, useContext, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Modal from "react-modal";
import AutoAppyLogin from "src/features/Auth/AutoAppyLogin";
import DeveloperOAuthCallback from "src/features/Auth/DeveloperOAuthCallback";
import { AuthContext } from "src/context/AuthContext";
import StageSidebar from "src/stage/StageSidebar";
import ProtectedRoute from "./ProtectedRoute";
import "./styles.css";
import useMediaQuery from "src/hooks/UseMediaQuery";

// AUTO-AI-17: every page loads on demand, so opening Chat does not download the flow
// editor, the guide or the sample landing pages. The sidebar shell stays in the main bundle.
const page = (load, name = "default") => lazy(() => load().then((module) => ({ default: module[name] })));
const OverviewPage = page(() => import("src/features/AiWorkspace/WorkspacePages"), "OverviewPage");
const NewBotPage = page(() => import("src/features/AiWorkspace/WorkspacePages"), "NewBotPage");
const BotDestination = page(() => import("src/features/AiWorkspace/WorkspacePages"), "BotDestination");
const ContactFieldsPage = page(() => import("src/features/AiWorkspace/WorkspacePages"), "ContactFieldsPage");
const ActivityPage = page(() => import("src/features/Conversations/ConversationsPage"));
const ChatPage = page(() => import("src/features/Chat/ChatPage"));
const VoicePage = page(() => import("src/features/Voice/VoicePage"));
const StudioPage = page(() => import("src/features/Studio/StudioPage"));
const AssistantEditor = page(() => import("src/features/Assistants/AssistantEditor"));
const DevelopersPage = page(() => import("src/features/Developers/DevelopersPage"));
const SettingsPage = page(() => import("src/features/Settings/SettingsPage"));
const UsagePage = page(() => import("src/features/AiWorkspace/UsagePage"));
const GuidePage = page(() => import("src/features/AiWorkspace/GuidePage"));
const StaticFlowsPage = page(() => import("src/features/StaticFlows/StaticFlowsPage"));
const MemoryBotsPage = page(() => import("src/features/MemoryBots/MemoryBotsPage"));
const AutomationPage = page(() => import("src/features/Automation/AutomationPage"));
const BotWorkspace = page(() => import("src/features/Bots/BotWorkspace"));
const FaqBotsList = page(() => import("src/features/FaqBots/FaqBotsList"));
const FaqBotWorkspace = page(() => import("src/features/FaqBots/FaqBotWorkspace"));
const KnowledgeBotsList = page(() => import("src/features/KnowledgeBots/KnowledgeBotsList"));
const KnowledgeBotWorkspace = page(() => import("src/features/KnowledgeBots/KnowledgeBotWorkspace"));
const ProductBotWizard = page(() => import("src/features/ProductBots/ProductBotWizard"));
const ProductBotWorkspace = page(() => import("src/features/ProductBots/ProductBotWorkspace"));
const IntakeBotWizard = page(() => import("src/features/IntakeBots/IntakeBotWizard"));
const IntakeBotWorkspace = page(() => import("src/features/IntakeBots/IntakeBotWorkspace"));
const AiJobsList = page(() => import("src/features/AiJobs/AiJobsList"));
const AiJobDetail = page(() => import("src/features/AiJobs/AiJobDetail"));
const ThemePage = page(() => import("src/features/Theme/ThemePage"));
const SampleLandingPage = page(() => import("src/features/SampleLandingPage/LandingPage"));
const SampleUIPage = page(() => import("src/features/SampleUI/SampleUIPage"));

Modal.setAppElement("#root");

export function LibraryRedirect({ type, collection = "bots", editor }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const bot = params.get("bot");
  params.delete("bot");
  if (editor && bot)
    return (
      <Navigate replace to={`${editor}/${encodeURIComponent(bot)}?${params}`} />
    );
  params.set("type", type);
  params.set("collection", collection);
  params.set("view", "my-bots");
  return <Navigate replace to={`/bot-library?${params}`} />;
}

// Old addresses keep working: they land on the new page with the same organisation.
function MovedTo({ to, extra = {} }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  Object.entries(extra).forEach(([key, value]) => params.set(key, value));
  const query = params.toString();
  return <Navigate replace to={query ? `${to}?${query}` : to} />;
}

function RouteLoading() {
  return (
    <main className="ws ai-stage" aria-busy="true">
      <div className="ws-page">
        <span className="ws-skeleton" style={{ display: "block", width: 180, height: 14 }} />
        <span className="ws-skeleton" style={{ display: "block", width: "42%", height: 34, marginTop: 6 }} />
        <span className="ws-skeleton" style={{ display: "block", width: "100%", height: 220, borderRadius: 20, marginTop: 12 }} />
      </div>
    </main>
  );
}

const guarded = (element) => <ProtectedRoute element={element} />;

function AppRoutes() {
  const location = useLocation();
  // Below 1024px the sidebar is a drawer, as in the main app.
  const isDrawer = useMediaQuery("(max-width: 1024px)");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  useEffect(() => {
    if (!isSidebarOpen) return undefined;
    const close = (e) => {
      if (e.key === "Escape") setIsSidebarOpen(false);
    };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [isSidebarOpen]);
  useEffect(() => setIsSidebarOpen(false), [location.pathname]);
  const { auth } = useContext(AuthContext);
  const publicPaths = [
    "/",
    "/login",
    "/theme",
    "/sample",
    "/auth/error",
    "/auth/autoappy/developer/callback",
  ];
  const isPublicPath =
    publicPaths.includes(location.pathname) ||
    location.pathname.startsWith("/sampleui");
  const showSidebar =
    auth && !isPublicPath && !location.pathname.startsWith("/static-flow/");

  return (
    <div className={`app-container full-screen${showSidebar ? " ai-shell" : ""}`}>
      {showSidebar && (
        <StageSidebar
          mobileOpen={isDrawer && isSidebarOpen}
          setMobileOpen={setIsSidebarOpen}
        />
      )}

      <div
        className={`main-content${showSidebar ? " ai-main" : ""}`}
        inert={showSidebar && isDrawer && isSidebarOpen}
      >
        <Suspense fallback={<RouteLoading />}>
          <Routes>
            <Route path="/" element={<AutoAppyLogin />} />
            <Route path="/login" element={<AutoAppyLogin />} />
            <Route path="/auth/error" element={<AutoAppyLogin />} />
            <Route path="/auth/autoappy/developer/callback" element={<DeveloperOAuthCallback />} />
            <Route path="/theme" element={<ThemePage />} />
            <Route path="/sample" element={<SampleLandingPage />} />
            <Route path="/sampleui/*" element={<SampleUIPage />} />

            {/* Daily */}
            <Route path="/dashboard" element={guarded(<OverviewPage />)} />
            <Route path="/chat/:threadId?" element={guarded(<ChatPage />)} />
            <Route path="/voice/:tab?" element={guarded(<VoicePage />)} />
            <Route path="/activity" element={guarded(<ActivityPage />)} />

            {/* Studio: assistants, customer bots, tools and knowledge in one place */}
            <Route path="/bot-library" element={guarded(<StudioPage />)} />
            <Route path="/bot-library/type/:type" element={guarded(<StudioPage />)} />
            <Route path="/studio" element={<MovedTo to="/bot-library" />} />
            <Route path="/tools" element={guarded(<StudioPage tab="tools" />)} />
            <Route path="/assistants" element={<MovedTo to="/bot-library" extra={{ view: "assistants" }} />} />
            <Route path="/assistants/:assistantId" element={guarded(<AssistantEditor />)} />
            <Route path="/bot-library/new" element={guarded(<NewBotPage />)} />
            <Route path="/bots/:botKey" element={guarded(<BotDestination />)} />
            <Route path="/static-flows" element={guarded(<LibraryRedirect type="static_flow" editor="/static-flow" />)} />
            <Route path="/static-flow/:botId" element={guarded(<StaticFlowsPage />)} />
            <Route path="/sales-bots" element={guarded(<LibraryRedirect type="sales" editor="/sales-bots" />)} />
            <Route path="/sales-bots/:botId" element={guarded(<MemoryBotsPage salesMode />)} />
            <Route path="/memory-bots" element={guarded(<LibraryRedirect type="memory" editor="/memory-bots" />)} />
            <Route path="/memory-bots/:botId" element={guarded(<MemoryBotsPage />)} />
            <Route path="/faq-bots" element={guarded(<LibraryRedirect type="faq" collection="sources" />)} />
            <Route path="/faq-bots/new" element={guarded(<FaqBotsList createOnly />)} />
            <Route path="/faq-bots/:faqBotId" element={guarded(<FaqBotWorkspace />)} />
            <Route path="/knowledge-bots" element={guarded(<LibraryRedirect type="knowledge" collection="sources" />)} />
            <Route path="/knowledge-bots/new" element={guarded(<KnowledgeBotsList createOnly />)} />
            <Route path="/knowledge-bots/:knowledgeBotId" element={guarded(<KnowledgeBotWorkspace />)} />
            <Route path="/product-bots" element={guarded(<LibraryRedirect type="product" collection="sources" />)} />
            <Route path="/product-bots/new" element={guarded(<ProductBotWizard />)} />
            <Route path="/product-bots/:productBotId" element={guarded(<ProductBotWorkspace />)} />
            <Route path="/intake-bots" element={guarded(<LibraryRedirect type="intake" collection="sources" />)} />
            <Route path="/intake-bots/new" element={guarded(<IntakeBotWizard />)} />
            <Route path="/intake-bots/:intakeBotId" element={guarded(<IntakeBotWorkspace />)} />
            <Route path="/tools/faq-chatbot" element={guarded(<LibraryRedirect type="faq" />)} />
            <Route path="/tools/:botKey" element={guarded(<BotWorkspace />)} />
            <Route path="/ai-jobs" element={guarded(<AiJobsList />)} />
            <Route path="/ai-jobs/:id" element={guarded(<AiJobDetail />)} />

            {/* Connect */}
            <Route path="/connections" element={guarded(<AutomationPage />)} />
            <Route path="/automation" element={guarded(<AutomationPage />)} />
            <Route path="/developers" element={guarded(<DevelopersPage />)} />
            <Route path="/ai-api-keys" element={<MovedTo to="/developers" extra={{ tab: "keys" }} />} />

            {/* Footer */}
            <Route path="/usage" element={guarded(<UsagePage />)} />
            <Route path="/settings" element={guarded(<SettingsPage />)} />
            <Route path="/settings/:section" element={guarded(<SettingsPage />)} />
            <Route path="/contact-fields" element={guarded(<ContactFieldsPage />)} />
            <Route path="/guide" element={guarded(<GuidePage />)} />
            <Route path="/guide/bots/:type" element={guarded(<GuidePage />)} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}

export default AppRoutes;
